import React, {useEffect, useState} from 'react';
import {Alert, Button, Col, Row, Table, Tag, Typography} from 'antd';
import {NodeIndexOutlined, ReloadOutlined} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {agentRoutingConfigService} from '../../services/agentRoutingConfigService';

const {Title, Text} = Typography;

const MESSAGE_EVENT_TYPE_MAP: Record<string, string> = {
    TRADE_PROPOSAL: 'MESSAGE_TRADE_PROPOSAL_READY',
    WATCH_SIGNAL: 'MESSAGE_WATCH_SIGNAL_READY',
    RISK_REVIEW_REQUEST: 'MESSAGE_RISK_REVIEW_REQUEST_READY',
    RISK_REVIEW_REPLY: 'MESSAGE_RISK_REVIEW_REPLY_READY',
    TRADE_EXECUTION_RESULT: 'MESSAGE_TRADE_EXECUTION_RESULT_READY',
    TRADE_CANCELLED: 'MESSAGE_TRADE_CANCELLED_READY',
    TRADE_ACTIONS: 'MESSAGE_TRADE_ACTIONS_READY',
};

const EVENT_MESSAGE_TYPE_MAP: Record<string, string> = Object.fromEntries(
    Object.entries(MESSAGE_EVENT_TYPE_MAP).map(([messageType, eventType]) => [eventType, messageType])
);

const isMessageTrigger = (record: any): boolean => {
    if (record.triggerKind) {
        return record.triggerKind === 'MESSAGE';
    }
    return !!EVENT_MESSAGE_TYPE_MAP[record.eventType];
};

const getDisplayType = (record: any): string => {
    if (record.triggerKind === 'MESSAGE' && record.messageType) {
        return record.messageType;
    }
    return EVENT_MESSAGE_TYPE_MAP[record.eventType] || record.eventType || '';
};

const getBridgeEventType = (record: any): string => {
    if (record.triggerKind === 'MESSAGE' && record.messageType) {
        return MESSAGE_EVENT_TYPE_MAP[record.messageType] || '';
    }
    return record.eventType || '';
};

const AgentRoutingConfig: React.FC = () => {
    const [triggers, setTriggers] = useState<any[]>([]);
    const [outputs, setOutputs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const [t, o] = await Promise.all([
                agentRoutingConfigService.getAllTriggers(),
                agentRoutingConfigService.getAllOutputs()
            ]);
            setTriggers(t);
            setOutputs(o);
        } catch {
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const triggerColumns: ColumnsType<any> = [
        {title: '智能体ID', dataIndex: 'agentConfigId', key: 'agentConfigId', width: 100},
        {
            title: '触发来源', key: 'triggerSourceType', width: 100,
            render: (_, record) => (
                <Tag color={isMessageTrigger(record) ? 'purple' : 'blue'}>
                    {isMessageTrigger(record) ? '消息' : '事件'}
                </Tag>
            )
        },
        {
            title: '触发类型', key: 'triggerDisplayType', width: 220,
            render: (_, record) => (
                <Tag color={isMessageTrigger(record) ? 'purple' : 'geekblue'}>
                    {getDisplayType(record)}
                </Tag>
            )
        },
        {
            title: '底层事件码', key: 'eventType', width: 260,
            render: (_, record) => <Tag color="blue">{getBridgeEventType(record)}</Tag>
        },
        {title: '描述', dataIndex: 'description', key: 'description', ellipsis: true},
        {
            title: '启用', dataIndex: 'isActive', key: 'isActive', width: 70,
            render: (v) => v === 1 ? <Tag color="green">启用</Tag> : <Tag color="default">禁用</Tag>
        },
    ];

    const outputColumns: ColumnsType<any> = [
        {title: '智能体ID', dataIndex: 'agentConfigId', key: 'agentConfigId', width: 100},
        {
            title: '消息类型', dataIndex: 'messageType', key: 'messageType', width: 200,
            render: (v) => <Tag color="purple">{v}</Tag>
        },
        {title: '产出类型', dataIndex: 'outputKind', key: 'outputKind', width: 100},
        {
            title: '路由目标ID', dataIndex: 'targetAgentConfigId', key: 'targetAgentConfigId', width: 120,
            render: (v) => v ? <Tag color="cyan">{v}</Tag> : <Tag>无</Tag>
        },
        {title: '描述', dataIndex: 'description', key: 'description', ellipsis: true},
        {
            title: '启用', dataIndex: 'isActive', key: 'isActive', width: 70,
            render: (v) => v === 1 ? <Tag color="green">启用</Tag> : <Tag color="default">禁用</Tag>
        },
    ];

    return (
        <div style={{padding: 24}}>
            <Row justify="space-between" align="middle" style={{marginBottom: 16}}>
                <Col>
                    <Title level={4} style={{margin: 0}}>
                        <NodeIndexOutlined style={{marginRight: 8}}/>
                    </Title>
                </Col>
                <Col>
                    <Button icon={<ReloadOutlined/>} onClick={loadData} loading={loading}/>
                </Col>
            </Row>
            <Alert type="info" showIcon style={{marginBottom: 16}}
                   message={<Text type="secondary">路由规则由智能体配置自动生成，请在"智能体配置"页面中修改触发和产出规则。消息触发在底层以
                       `MESSAGE_*_READY` 事件码存储和调度。</Text>}/>
            <Row justify="space-between" align="middle" style={{marginBottom: 8}}>
                <Col><Title level={5} style={{margin: 0}}>事件→智能体 路由（全局总览）</Title></Col>
            </Row>
            <Table columns={triggerColumns} dataSource={triggers} rowKey="id" loading={loading}
                   pagination={false} size="small" style={{marginBottom: 24}}/>
            <Row justify="space-between" align="middle" style={{marginBottom: 8}}>
                <Col><Title level={5} style={{margin: 0}}>智能体→消息 路由（全局总览）</Title></Col>
            </Row>
            <Table columns={outputColumns} dataSource={outputs} rowKey="id" loading={loading}
                   pagination={false} size="small"/>
        </div>
    );
};

export default AgentRoutingConfig;
